<template>
  <div>
    <!-- 充值主弹框 -->
    <el-dialog
      v-model="visible"
      width="960px"
      :show-close="false"
      class="recharge-dialog"
      @closed="onClosed"
    >
      <button class="recharge-close" aria-label="关闭" @click="visible = false">
        <el-icon><Close /></el-icon>
      </button>

      <div class="recharge-wrap">
        <!-- 左侧背景图 -->
        <div class="recharge-aside" aria-hidden="true"></div>

        <!-- 右侧套餐区 -->
        <div class="recharge-main">
          <h2 class="main-title">充值 AI 豆，开始智能出图</h2>
          <p class="main-subtitle">AI豆是平台通用积分，消耗后可续费购买，永久不过期</p>

          <div class="bundle-row">
            <button
              v-for="b in bundles"
              :key="b.id"
              type="button"
              class="bundle-card"
              :class="{ active: !custom && selectedBundleId === b.id }"
              @click="selectBundle(b)"
            >
              <span v-if="b.tag" class="bundle-tag">{{ b.tag }}</span>
              <div class="bundle-name">{{ b.name }}</div>
              <div class="bundle-price">
                <span class="price-symbol">¥</span>
                <span class="price-num">{{ priceText(b.price) }}</span>
              </div>
              <div class="bundle-origin" v-if="b.originPrice">原价 ¥{{ priceText(b.originPrice) }}</div>
              <div class="bundle-amount" :class="{ 'is-empty': !bundleAmount(b) }">
                充值 {{ bundleAmount(b) || '—' }} AI豆
              </div>
            </button>
          </div>

          <button
            type="button"
            class="custom-row"
            :class="{ active: custom }"
            @click="custom = true"
          >
            <span class="custom-radio" :class="{ checked: custom }">
              <span v-if="custom" class="radio-dot"></span>
            </span>
            <span class="custom-label">自定义充值</span>
            <span class="custom-unit">1元/豆</span>
            <input
              v-model.lazy="customInput"
              class="custom-input"
              :class="{ active: custom }"
              placeholder="请输入充值数量"
              inputmode="numeric"
              @focus="custom = true"
              @click.stop
            />
            <span class="custom-suffix" v-if="custom">AI豆</span>
          </button>

          <label class="agree-box">
            <span
              class="agree-checkbox"
              :class="{ checked: agree }"
              role="checkbox"
              :aria-checked="agree"
              tabindex="0"
              @click.prevent="agree = !agree"
              @keydown.space.prevent="agree = !agree"
            >
              <el-icon v-if="agree"><Check /></el-icon>
            </span>
            <span class="agree-text">
              我已阅读并同意
              <a href="javascript:;" @click.stop="openAgreement('user_recharge_agreement', '用户充值协议')">《用户充值协议》</a>
              和<a href="javascript:;" @click.stop="openAgreement('ai_bean_use_agreement', 'AI豆使用规则')">《AI豆使用规则》</a>
            </span>
          </label>

          <div class="footer-actions">
            <button type="button" class="footer-btn footer-btn--ghost" @click="visible = false">稍后再说</button>
            <button type="button" class="footer-btn footer-btn--primary" :class="{ disabled: !canSubmit || submitting, loading: submitting }" :disabled="!canSubmit || submitting" @click="confirm">
              <span v-if="submitting" class="btn-spinner"></span>
              <span>{{ submitting ? '处理中…' : '立即购买' }}</span>
            </button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 支付二维码弹框 -->
    <el-dialog
      v-model="payVisible"
      width="360px"
      :show-close="false"
      class="pay-dialog"
      :close-on-click-modal="false"
      @closed="closePay"
    >
      <button class="pay-close" aria-label="关闭" @click="closePay">
        <el-icon><Close /></el-icon>
      </button>
      <div class="pay-wrap">
        <h3 class="pay-title">微信扫码支付</h3>

        <div class="pay-header">
          <div class="pay-amount">
            <span class="pay-amount-symbol">¥</span>
            <span class="pay-amount-num">{{ payAmountText }}</span>
          </div>
        </div>

        <div class="pay-qr-box">
          <div class="pay-qr-deco pay-qr-deco--tl"></div>
          <div class="pay-qr-deco pay-qr-deco--tr"></div>
          <div class="pay-qr-deco pay-qr-deco--bl"></div>
          <div class="pay-qr-deco pay-qr-deco--br"></div>
          <qrcode-vue :value="codeUrl" :size="180" level="H" v-if="codeUrl" />
        </div>

        <div class="pay-scan">
          <span class="pay-scan-text">打开手机微信，扫一扫完成支付</span>
        </div>

        <div class="pay-info">
          <div class="pay-info-row">
            <span class="pay-info-label">商品信息</span>
            <span class="pay-info-value">{{ payBeanAmountText }}</span>
          </div>
          <div class="pay-info-row">
            <span class="pay-info-label">支付金额</span>
            <span class="pay-info-value pay-info-value--amount">¥ {{ payAmountText }}</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 协议弹框 -->
    <el-dialog
      v-model="agreementVisible"
      width="640px"
      class="agreement-dialog"
      append-to-body
    >
      <template #header>
        <div class="agreement-title">{{ agreementTitle }}</div>
      </template>
      <div class="agreement-body" v-loading="agreementLoading">
        <div v-if="!agreementLoading && agreementContent" class="agreement-content" v-html="agreementContent"></div>
        <el-empty v-else-if="!agreementLoading" description="暂无协议内容" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue'
import { http } from '@dh/dh-for-vue'
import { ElMessage } from 'element-plus'
import {
  Close,
  Check,
} from '@element-plus/icons-vue'
import QrcodeVue from 'qrcode.vue'

interface Bundle {
  id: number
  name: string
  beanAmount: number
  price: number
  originPrice: number
  tag?: string
}

const visible = ref(false)
const payVisible = ref(false)

const bundles = ref<Bundle[]>([])
const selectedBundleId = ref<number>()
const custom = ref(false)
const customAmount = ref<number>(1)
const customInput = ref<string>('')
const agree = ref(true)

const codeUrl = ref('')
const currentOrderNo = ref('')
const payAmountFen = ref(0)
const payBeanAmount = ref<number>(0)
const payBundleName = ref('')
const polling = ref(false)
const submitting = ref(false)
let timer: any = null

const agreementVisible = ref(false)
const agreementLoading = ref(false)
const agreementTitle = ref('')
const agreementContent = ref('')

const customAmountNum = computed(() => {
  const v = parseInt(customInput.value, 10)
  return Number.isFinite(v) && v > 0 ? v : 0
})

const canSubmit = computed(() => {
  if (!agree.value) return false
  if (custom.value) return customAmountNum.value > 0
  return !!selectedBundleId.value
})

const payAmountText = computed(() => {
  const fen = payAmountFen.value || 0
  return Number(fen / 100).toFixed(fen % 100 === 0 ? 0 : 2)
})

const payBeanAmountText = computed(() => {
  if (payBundleName.value) return payBundleName.value
  const amount = payBeanAmount.value
  return amount > 0 ? `AI豆 × ${amount.toLocaleString()}` : 'AI豆充值'
})

function priceText(fen: number) {
  return Number(fen / 100).toFixed(fen % 100 === 0 ? 0 : 2)
}

function bundleAmount(b: Bundle) {
  return b.beanAmount?.toLocaleString?.() || ''
}

function selectBundle(b: Bundle) {
  custom.value = false
  selectedBundleId.value = b.id
}

watch(customInput, () => {
  customAmount.value = customAmountNum.value
})

async function loadBundles() {
  const res: any = await http.get('/institutionAdmin/aiBeanOrder/bundleList')
  bundles.value = res.data?.data || []
  if (bundles.value.length && !custom.value) {
    selectedBundleId.value = bundles.value[0].id
  }
}

function open() {
  visible.value = true
  agree.value = true
  custom.value = false
  selectedBundleId.value = undefined
  customAmount.value = 1
  customInput.value = ''
  codeUrl.value = ''
  payAmountFen.value = 0
  payBeanAmount.value = 0
  payBundleName.value = ''
  loadBundles()
}

function onClosed() {
  // 主弹框关闭时不应清掉支付轮询定时器，否则会误杀支付二维码弹框的轮询
}

async function confirm() {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  try {
    const payload: any = { custom: custom.value }
    if (custom.value) {
      payload.beanAmount = customAmountNum.value
    } else {
      payload.bundleId = selectedBundleId.value
    }
    const res: any = await http.post('/institutionAdmin/aiBeanOrder/create', payload)
    const data = res.data?.data
    if (!data) {
      ElMessage.error('下单失败')
      return
    }
    currentOrderNo.value = data.orderNo
    payAmountFen.value = data.amount ?? data.price ?? (custom.value ? customAmountNum.value * 100 : (bundles.value.find(b => b.id === selectedBundleId.value)?.price || 0))
    if (custom.value) {
      payBundleName.value = ''
      payBeanAmount.value = customAmountNum.value
    } else {
      const b = bundles.value.find(x => x.id === selectedBundleId.value)
      payBundleName.value = b?.name || ''
      payBeanAmount.value = b?.beanAmount || 0
    }
    const nativeRes: any = await http.get(`/api/pay/wx/native/${data.tradeId}`)
    const nativeData = nativeRes.data?.data
    codeUrl.value = nativeData?.codeUrl || ''
    if (!codeUrl.value) {
      ElMessage.error('获取支付二维码失败')
      return
    }
    visible.value = false
    payVisible.value = true
    // 延迟启动轮询，确保主弹框的 @closed 动画已结束，
    // 避免因 onClosed 不再清定时器之外的时序问题导致首轮轮询丢失
    setTimeout(startPoll, 350)
  } finally {
    submitting.value = false
  }
}

function startPoll() {
  stopTimers()
  timer = setInterval(() => pollOnce(), 2000)
}

async function pollOnce() {
  if (!currentOrderNo.value) return
  polling.value = true
  try {
    const res: any = await http.get(`/institutionAdmin/aiBeanOrder/detail?orderNo=${currentOrderNo.value}`)
    const row = res.data?.data
    if (row && row.status === 'PAID') {
      stopTimers()
      ElMessage.success('支付成功，AI豆已到账')
      payVisible.value = false
      emit('success')
    }
  } finally {
    polling.value = false
  }
}

function closePay() {
  stopTimers()
  payVisible.value = false
}

async function openAgreement(code: string, title: string) {
  agreementTitle.value = title
  agreementContent.value = ''
  agreementVisible.value = true
  agreementLoading.value = true
  try {
    const res: any = await http.get(`/api/article/c/${code}`)
    agreementContent.value = res.data?.data?.content || ''
  } catch (e) {
    ElMessage.error('协议加载失败')
  } finally {
    agreementLoading.value = false
  }
}

function stopTimers() {
  if (timer) { clearInterval(timer); timer = null }
}

onUnmounted(stopTimers)

const emit = defineEmits<{ (e: 'success'): void }>()

defineExpose({ open })
</script>

<style scoped lang="scss">
.recharge-dialog {
  :deep(.el-dialog) {
    border-radius: 18px;
    overflow: hidden;
  }
  :deep(.el-dialog__header) {
    display: none;
  }
  :deep(.el-dialog__body) {
    padding: 0;
    background: #fff;
  }
  :deep(.el-overlay) {
    background-color: rgba(20, 18, 60, 0.45);
  }
}

.recharge-close {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 5;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  color: #8a8aa3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #fff;
    color: #6d55d8;
    transform: scale(1.05);
  }
}

.recharge-wrap {
  display: flex;
  min-height: 580px;
}

/* ====== 左侧区域占位（背景色，文案待定） ====== */
.recharge-aside {
  width: 280px;
  flex-shrink: 0;
  background-image: url('https://pic.xuefou.com/pic/20260810/1800/e2eeec07b93e48a79785878cdc165d3c.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 18px 0 0 18px;
}

/* ====== 右侧 ====== */
.recharge-main {
  flex: 1 1 0;
  min-width: 0;
  padding: 28px 36px 26px;
  display: flex;
  flex-direction: column;
}

.main-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.main-subtitle {
  font-size: 13px;
  color: #767690;
  margin: 0 0 22px 0;
}

.bundle-row {
  display: flex;
  gap: 12px;
  margin: 0 -8px 18px;
  padding: 18px 8px 8px;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e0daf5;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.bundle-card {
  flex: 0 0 140px;
  position: relative;
  background: #fff;
  border: 1.5px solid #e8e3ff;
  border-radius: 14px;
  padding: 18px 12px 14px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: #b8a8f5;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(109, 85, 216, 0.12);
  }

  &.active {
    border-color: #6d55d8;
    background: #f0edff;
    box-shadow: 0 8px 22px rgba(109, 85, 216, 0.2);
  }

  .bundle-tag {
    position: absolute;
    top: -10px;
    left: 12px;
    background: linear-gradient(to right, #ff9a3c, #ff7a45);
    color: #fff;
    font-size: 11px;
    padding: 2px 10px;
    border-radius: 10px;
    box-shadow: 0 4px 10px rgba(255, 122, 69, 0.3);
  }

  .bundle-name {
    font-size: 14px;
    color: #5b5b75;
    margin-bottom: 6px;
    font-weight: 500;
  }

  .bundle-price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 4px;

    .price-symbol {
      font-size: 14px;
      color: #1a1a2e;
      font-weight: 600;
      margin-right: 1px;
    }

    .price-num {
      font-size: 30px;
      color: #1a1a2e;
      font-weight: 800;
      line-height: 1;
    }
  }

  .bundle-origin {
    font-size: 12px;
    color: #b8b8c8;
    text-decoration: line-through;
    margin-bottom: 10px;
  }

  .bundle-amount {
    font-size: 12px;
    font-weight: 500;
    padding: 6px 10px;
    background: #f5f3ff;
    color: #6d55d8;
    border-radius: 8px;
    display: inline-block;
    min-width: 80%;

    &.is-empty {
      background: #f5f5fa;
      color: #b8b8c8;
    }
  }

  &.active .bundle-price {
    .price-symbol,
    .price-num {
      color: #6d55d8;
    }
  }

  &.active .bundle-amount {
    background: #e7e1ff;
    color: #6d55d8;
  }
}

.custom-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1.5px solid #e8e3ff;
  border-radius: 12px;
  margin-bottom: 18px;
  transition: all 0.2s;
  background: #fff;
  cursor: pointer;
  font-family: inherit;

  &.active {
    border-color: #6d55d8;
    background: #faf8ff;
  }

  .custom-radio {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid #cbc4e4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;

    .radio-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #6d55d8;
    }

    &.checked {
      border-color: #6d55d8;
    }
  }

  .custom-label {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a2e;
  }

  .custom-unit {
    font-size: 12px;
    color: #767690;
    background: #f5f3ff;
    color: #6d55d8;
    padding: 2px 8px;
    border-radius: 6px;
  }

  .custom-input {
    flex: 1;
    border: none;
    background: transparent;
    text-align: right;
    font-size: 14px;
    color: #1a1a2e;
    outline: none;
    font-family: inherit;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background 0.2s;

    &::placeholder { color: #b8b8c8; }

    &.active {
      background: rgba(109, 85, 216, 0.06);
    }
  }

  .custom-suffix {
    font-size: 13px;
    color: #767690;
    font-weight: 500;
  }
}

.agree-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  cursor: pointer;
  user-select: none;
  padding: 4px 0;

  .agree-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid #cbc4e4;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 11px;
    flex-shrink: 0;
    transition: all 0.2s;

    &.checked {
      background: #6d55d8;
      border-color: #6d55d8;
    }
  }

  .agree-text {
    font-size: 12px;
    color: #767690;

    a {
      color: #6d55d8;
      text-decoration: none;
      font-weight: 500;

      &:hover { text-decoration: underline; }
    }
  }
}

.footer-actions {
  display: flex;
  gap: 14px;
}

.footer-btn {
  flex: 1;
  height: 44px;
  border-radius: 22px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;

  &--ghost {
    background: #f3f2f8;
    color: #767690;

    &:hover { background: #ebeaf3; }
  }

  &--primary {
    background: linear-gradient(to right, #7c5cf0, #a78bfa);
    color: #fff;
    box-shadow: 0 8px 20px rgba(109, 85, 216, 0.38);

    &:hover { transform: translateY(-1px); box-shadow: 0 10px 26px rgba(109, 85, 216, 0.46); }

    &.disabled, &:disabled {
      background: #cbc4e4;
      box-shadow: none;
      cursor: not-allowed;
      transform: none;
    }

    &.loading {
      cursor: wait;
      opacity: 0.92;
    }

    .btn-spinner {
      width: 15px;
      height: 15px;
      margin-right: 6px;
      border: 2px solid rgba(255, 255, 255, 0.45);
      border-top-color: #fff;
      border-radius: 50%;
      display: inline-block;
      vertical-align: -2px;
      animation: btn-spin 0.7s linear infinite;
    }
  }
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

/* ====== 支付弹框 ====== */
.pay-dialog {
  :deep(.el-dialog) {
    border-radius: 16px;
    overflow: hidden;
  }
  :deep(.el-dialog__header) {
    display: none;
  }
  :deep(.el-dialog__body) {
    padding: 0;
  }
}

.pay-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 5;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.04);
  color: #9a9ab0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: #5b5b75;
  }
}

.pay-wrap {
  padding: 24px 28px 20px;
  text-align: center;
}

.pay-title {
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  text-align: center;
}

.pay-header {
  margin-bottom: 18px;

  .pay-amount {
    display: flex;
    align-items: baseline;
    justify-content: center;
    color: #1a1a2e;

    .pay-amount-symbol {
      font-size: 20px;
      font-weight: 700;
      margin-right: 2px;
    }
    .pay-amount-num {
      font-size: 34px;
      font-weight: 800;
      line-height: 1;
    }
  }
}

.pay-qr-box {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(20, 18, 60, 0.08);

  .pay-qr-deco {
    position: absolute;
    width: 14px;
    height: 14px;
    border: 2px solid #6d55d8;

    &--tl { top: 8px; left: 8px; border-right: none; border-bottom: none; border-radius: 4px 0 0 0; }
    &--tr { top: 8px; right: 8px; border-left: none; border-bottom: none; border-radius: 0 4px 0 0; }
    &--bl { bottom: 8px; left: 8px; border-right: none; border-top: none; border-radius: 0 0 0 4px; }
    &--br { bottom: 8px; right: 8px; border-left: none; border-top: none; border-radius: 0 0 4px 0; }
  }
}

.pay-scan {
  width: 100%;
  height: 44px;
  margin: 0 0 20px;
  border-radius: 10px;
  background: #07c160;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  .pay-scan-text {
    font-size: 14px;
    color: #fff;
    font-weight: 600;
  }
}

.pay-info {
  border-top: 1px solid #f0f0f5;
  padding-top: 16px;
  text-align: left;

  .pay-info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .pay-info-label {
    font-size: 13px;
    color: #9999a8;
  }

  .pay-info-value {
    font-size: 13px;
    color: #1a1a2e;
    font-weight: 500;

    &--amount {
      font-size: 15px;
      font-weight: 700;
    }
  }
}

.agreement-dialog {
  :deep(.el-dialog) {
    border-radius: 12px;
  }

  .agreement-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    text-align: center;
  }

  .agreement-body {
    min-height: 200px;
    max-height: 60vh;
    overflow-y: auto;
  }

  .agreement-content {
    font-size: 14px;
    line-height: 1.8;
    color: #33334d;

    :deep(p) {
      margin: 0 0 12px;
    }
    :deep(img) {
      max-width: 100%;
      height: auto;
    }
    :deep(h1, h2, h3, h4) {
      margin: 16px 0 10px;
      color: #1a1a2e;
    }
    :deep(a) {
      color: #6d55d8;
    }
    :deep(ul, ol) {
      padding-left: 20px;
    }
  }
}
</style>
